import { MimeType } from 'vet-bones/constants';

import {
  FileUpload,
  Mapping,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import {
  ProjectFileUpload,
  ProjectFileUploadOptions,
  defaultProjectFileUploadOptions,
} from 'src/store/state/projects';
import { newline } from 'src/templates/copy';
import { ExportFileUploadOptions } from 'src/utils/export/types';
import { ExportToFileOptions } from 'src/utils/exportToFile';
import { stringifyFileUploadMappings } from 'src/utils/stringify/mappings/fileUploadMappings';
import { stringifyProperties } from 'src/utils/stringify/properties';
import { stringToFilename } from 'src/utils/stringToFilename';

const exportMappings = ({
  classMappings,
  diagram,
  name,
  relationshipMappings,
  dataNamespace,
}: {
  classMappings: Mapping[];
  diagram: DiagramSnapshot;
  name: string;
  relationshipMappings: RelationshipMapping[];
  dataNamespace: string;
}) => {
  const filename = `${stringToFilename(name)}.sms`;
  const body = stringifyFileUploadMappings({
    classMappings,
    model: diagram.model,
    relationshipMappings,
    dataNamespace,
  });
  const contents = body + newline;
  return { contents, filename, mimeType: MimeType.SMS };
};

const exportProperties = (name: string, options: ProjectFileUploadOptions) => {
  const filename = `${stringToFilename(name)}.properties`;
  const properties: Record<string, string | boolean> = {
    'csv.header': options.header,
    'csv.quote': options.quote,
    'csv.separator': options.separator,
    'csv.skip.empty': options.skipEmpty,
  };

  // Do not set 'csv.escape' to the same value as 'csv.quote'
  // https://docs.stardog.com/virtual-graphs/importing-json-csv-files#csv-properties-file
  if (options.escape !== options.quote) {
    properties['csv.escape'] = options.escape;
  }

  const contents = stringifyProperties(properties) + newline;
  return { contents, filename, mimeType: MimeType.TEXT };
};

const exportCsv = (name: string, contents: string) => {
  const filename = `${stringToFilename(name)}.csv`;
  return { contents, filename, mimeType: MimeType.CSV };
};

export const exportFileUpload = ({
  classMappings,
  csvOptions: csvOpts,
  data,
  diagram,
  fileUpload,
  relationshipMappings,
  dataNamespace,
}: {
  classMappings: Mapping[];
  csvOptions?: ExportToFileOptions | null;
  data: FileUpload;
  diagram: DiagramSnapshot;
  fileUpload: ProjectFileUpload;
  relationshipMappings: RelationshipMapping[];
  dataNamespace: string;
}): ExportFileUploadOptions => {
  const fileUploadName = data.text;

  const csvOptions =
    csvOpts ||
    exportCsv(
      fileUpload.partial ? `${fileUploadName}_SAMPLE` : fileUploadName,
      fileUpload.contents || ''
    );
  const mappingsOptions = exportMappings({
    classMappings,
    diagram,
    name: fileUploadName,
    relationshipMappings,
    dataNamespace,
  });
  const propertiesOptions = exportProperties(
    fileUploadName,
    fileUpload.options || defaultProjectFileUploadOptions
  );

  return { csvOptions, mappingsOptions, propertiesOptions };
};
